import { useState, useEffect } from 'react';
import { formatDatePT } from '../utils/dateFormat';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Calendar, CalendarDays, Download, FileText, Image as ImageIcon, Plane, Edit, Wallet, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Sequential image compression for mobile-safe uploads — iteratively downscales +
// lowers quality until file fits target size (~10MB). Uses URL.createObjectURL so
// 50-80MB ProRAW / HEIF originals don't OOM-crash on mobile.
const compressImage = (file, targetBytes = 10 * 1024 * 1024) => {
  return new Promise((resolve) => {
    if (!file.type || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    if (file.size <= 2 * 1024 * 1024) {
      resolve(file);
      return;
    }
    const objUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      const attempts = [
        { maxW: 2560, q: 0.92 },
        { maxW: 2560, q: 0.85 },
        { maxW: 2048, q: 0.85 },
        { maxW: 1920, q: 0.82 },
        { maxW: 1920, q: 0.75 },
        { maxW: 1600, q: 0.75 },
      ];
      const canvas = document.createElement('canvas');
      let lastBlob = null;
      try {
        for (let i = 0; i < attempts.length; i++) {
          const { maxW, q } = attempts[i];
          const ratio = Math.min(maxW / img.width, 1);
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', q));
          if (!blob) continue;
          lastBlob = blob;
          if (blob.size <= targetBytes) break;
        }
      } catch {/* noop */}
      canvas.width = 0; canvas.height = 0;
      URL.revokeObjectURL(objUrl);
      if (lastBlob && lastBlob.size < file.size) {
        const cleanName = (file.name || 'foto.jpg').replace(/\.(heic|heif|tiff|png|webp|dng)$/i, '.jpg');
        resolve(new File([lastBlob], cleanName, { type: 'image/jpeg' }));
      } else {
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(file); };
    img.src = objUrl;
  });
};

const TimeEntries = ({ user }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    hours_worked: '',
    travel_hours: '',
    lunch_hours: '',
    project: '',
    client: '',
    description: '',
    user_id: '', // Para admin selecionar funcionário
  });
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addParkeren, setAddParkeren] = useState(false);
  const [parkerenAmount, setParkerenAmount] = useState('');
  const [addExpense, setAddExpense] = useState(false);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [expenseData, setExpenseData] = useState({
    description: '',
    amount: '',
    receipt: null,
    category: 'material'
  });

  useEffect(() => {
    fetchClients();
    fetchTodaySchedule();
    if (user.is_admin) {
      fetchUsers();
    }
  }, [user.is_admin]);

  const hasFilter = !!(search.trim() || filterStart || filterEnd);

  useEffect(() => {
    const id = setTimeout(fetchEntries, hasFilter ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, filterStart, filterEnd]);

  const fetchEntries = async () => {
    try {
      const params = { limit: hasFilter ? 1000 : 100 };
      if (search.trim()) params.search = search.trim();
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;
      const response = await axiosInstance.get('/time-entries', { params });
      // Suporta tanto formato antigo (array) quanto novo (paginado)
      const data = response.data.entries || response.data;
      setEntries(Array.isArray(data) ? data : []);
      setTotalHours(response.data.total_hours || 0);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      toast.error(t('errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/clients/all');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  const fetchTodaySchedule = async () => {
    try {
      const response = await axiosInstance.get('/schedule/today');
      setTodaySchedule(response.data || []);
    } catch (error) {
      console.error('Error fetching today schedule');
    }
  };


  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users');
    }
  };

  const calculateHours = (start, end, lunchMinutes = 0) => {
    if (!start || !end) return 0;
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const totalHours = endHour - startHour + (endMin - startMin) / 60;
    // Converter minutos para horas
    const lunchHours = parseFloat(lunchMinutes) / 60 || 0;
    return Math.max(0, totalHours - lunchHours).toFixed(2);
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      // Modo edição
      setEditMode(true);
      setEditingEntry(entry);
      const entryClient = (entry.client || '').trim().toLowerCase();
      const clientMatch = clients.find(c => (c.name || '').trim().toLowerCase() === entryClient);
      setSelectedClientId(clientMatch ? clientMatch.id : '');
      
      // Preserve the address selection when editing
      if (clientMatch) {
        const addresses = clientMatch.addresses || [];
        const entryProject = (entry.project || '').trim().toLowerCase();
        const matchingAddr = addresses.find(a => (a.address || '').trim().toLowerCase() === entryProject);
        if (matchingAddr) {
          setSelectedAddressId(matchingAddr.id);
        } else if ((clientMatch.address || '').trim().toLowerCase() === entryProject) {
          setSelectedAddressId('primary');
        } else {
          setSelectedAddressId('');
        }
      } else {
        setSelectedAddressId('');
      }
      setFormData({
        date: entry.date,
        start_time: entry.start_time,
        end_time: entry.end_time,
        hours_worked: entry.hours_worked,
        lunch_hours: entry.lunch_hours || 0,
        project: entry.project,
        client: entry.client,
        description: entry.description || '',
        user_id: entry.user_id || '',
      });
    } else {
      // Modo criação
      setEditMode(false);
      setEditingEntry(null);
      setSelectedClientId('');
      setSelectedAddressId('');
      setFormData({
        date: '',
        start_time: '',
        end_time: '',
        hours_worked: '',
        lunch_hours: '',
        project: '',
        client: '',
        description: '',
        user_id: '',
      });
    }
    setSelectedFiles([]);
    setAddExpense(false);
    setExpenseData({ description: '', amount: '', receipt: null });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Validar endereço obrigatório quando cliente tem múltiplos endereços
    const selectedClient = clients.find(c => c.id === selectedClientId);
    if (selectedClient && selectedClient.addresses && selectedClient.addresses.length > 0 && !selectedAddressId && !formData.project) {
      toast.error('Selecione um endereço para este cliente');
      return;
    }

    setSubmitting(true);
    const hours = calculateHours(formData.start_time, formData.end_time, formData.lunch_hours);
    const formDataToSend = new FormData();

    formDataToSend.append('date', formData.date);
    formDataToSend.append('start_time', formData.start_time);
    formDataToSend.append('end_time', formData.end_time);
    formDataToSend.append('hours_worked', hours);
    formDataToSend.append('travel_hours', '0');
    formDataToSend.append('lunch_hours', formData.lunch_hours || '0');
    formDataToSend.append('project', formData.project);
    formDataToSend.append('client', formData.client);
    formDataToSend.append('description', formData.description || '');
    
    // Admin pode selecionar funcionário
    if (user.is_admin && formData.user_id) {
      formDataToSend.append('user_id', formData.user_id);
    }

    // IMPORTANT: do NOT include photo files here — upload them one-by-one AFTER entry is created.
    // This prevents huge multipart requests (timeout on Kubernetes ingress) and Android OOM
    // from parallel compression of many photos.
    
    // Adicionar parkeren como despesa automática
    if (addParkeren && parkerenAmount && !editMode) {
      formDataToSend.append('create_parkeren', 'true');
      formDataToSend.append('parkeren_amount', parkerenAmount);
    }
    
    // Adicionar dados de despesa (material) se checkbox estiver marcado
    if (addExpense && !editMode) {
      formDataToSend.append('create_expense', 'true');
      formDataToSend.append('expense_category', 'material');
      if (expenseData.description) {
        formDataToSend.append('expense_description', expenseData.description);
      }
      if (expenseData.amount) {
        formDataToSend.append('expense_amount', expenseData.amount);
      }
      if (expenseData.receipt) {
        formDataToSend.append('expense_receipt', expenseData.receipt);
      }
    }

    let entryId = editMode && editingEntry ? editingEntry.id : null;
    try {
      if (editMode && editingEntry) {
        await axiosInstance.put(`/time-entries/${editingEntry.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
      } else {
        const res = await axiosInstance.post('/time-entries', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
        entryId = res.data?.id;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fout bij opslaan registratie');
      setSubmitting(false);
      return;
    }

    // Upload photos sequentially (one-by-one) to avoid network/OOM issues
    let okPhotos = 0;
    let failPhotos = 0;
    if (selectedFiles.length > 0 && entryId) {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress(`Foto ${i + 1} van ${selectedFiles.length}: comprimeren...`);
        let toSend = file;
        try { toSend = await compressImage(file); } catch { /* fallback to original */ }
        setUploadProgress(`Foto ${i + 1} van ${selectedFiles.length}: uploaden (${(toSend.size / 1024 / 1024).toFixed(1)} MB)...`);
        const fd = new FormData();
        fd.append('file', toSend);
        try {
          await axiosInstance.post(`/time-entries/${entryId}/photos`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90000,
          });
          okPhotos++;
        } catch {
          failPhotos++;
        }
      }
      setUploadProgress('');
    }

    // Feedback
    const hasExpenses = (addParkeren && parkerenAmount) || (addExpense && expenseData.amount);
    const photoMsg = selectedFiles.length > 0
      ? ` · ${okPhotos} foto's${failPhotos ? `, ${failPhotos} mislukt` : ''}`
      : '';
    if (editMode) {
      toast.success(`Registratie bijgewerkt${photoMsg}`);
    } else if (hasExpenses) {
      toast.success(`Registratie + uitgave toegevoegd${photoMsg}`);
    } else {
      toast.success(`Registratie toegevoegd${photoMsg}`);
    }

    setOpen(false);
    setEditMode(false);
    setEditingEntry(null);
    setSelectedClientId('');
    setSelectedAddressId('');
    setSubmitting(false);
    setFormData({
      date: '',
      start_time: '',
      end_time: '',
      hours_worked: '',
      lunch_hours: '',
      project: '',
      client: '',
      description: '',
      user_id: '',
    });
    setSelectedFiles([]);
    setAddParkeren(false);
    setParkerenAmount('');
    setAddExpense(false);
    setExpenseData({ description: '', amount: '', receipt: null, category: 'material' });
    fetchEntries();
  };

  const handleDelete = async (id, status) => {
    if (status !== 'pending' && !user.is_admin) {
      toast.error('Não é possível deletar horas já aprovadas/rejeitadas');
      return;
    }

    if (!window.confirm('Deseja deletar este registro?')) return;

    try {
      await axiosInstance.delete(`/time-entries/${id}`);
      toast.success('Registro deletado!');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorDeletingEntry'));
    }
  };

  const handleDownload = async (entryId, fileId, filename) => {
    try {
      const response = await axiosInstance.get(`/time-entries/${entryId}/file/${fileId}`);
      const fileData = response.data.file_data;
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('download'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return <ImageIcon className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: t('pending'), color: 'bg-yellow-500' },
      approved: { label: t('approved'), color: 'bg-green-500' },
      rejected: { label: t('rejected'), color: 'bg-red-500' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`${badge.color} text-gray-800 px-2 py-1 rounded text-xs font-semibold`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-800 text-lg">{t('loading')}</div>
      </div>
    );
  }

  // Resolves the eindklant (real klant) from a stored time-entry: matches the
  // entry.project (= address) against the clients tree to upgrade old entries
  // where entry.client still holds the bedrijfsnaam instead of the label.
  const resolveEindklant = (entry) => {
    const out = { label: entry.client || '', bedrijf: '' };
    const proj = (entry.project || '').trim().toLowerCase();
    if (!proj) return out;
    for (const c of clients) {
      const m = (c.addresses || []).find(a => (a.address || '').trim().toLowerCase() === proj);
      if (m) {
        if (m.label) out.label = m.label;
        out.bedrijf = c.name;
        break;
      }
    }
    return out;
  };

  return (
    <div className="space-y-6" data-testid="time-entries-container">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-800">{t('timeEntries')}</h1>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            setEditMode(false);
            setEditingEntry(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button
              onClick={() => handleOpenDialog()}
              data-testid="add-entry-button"
              className="bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold px-6 py-6 rounded-xl transition-all duration-300 hover:scale-[1.02]"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('addTimeEntry')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="entry-form-description">
            <DialogHeader>
              <DialogTitle className="text-2xl text-gray-800">
                {editMode ? t('edit') : t('add')} {t('timeEntries')}
              </DialogTitle>
              <p id="entry-form-description" className="sr-only">
                {editMode ? 'Formulário para editar registro de horas' : 'Formulário para adicionar novo registro de horas'}
              </p>
            </DialogHeader>
            
            {/* Today's schedule suggestion */}
            {!editMode && todaySchedule.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="schedule-suggestions">
                <p className="text-blue-700 text-xs font-semibold mb-2 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" />
                  Agenda de hoje - clique para preencher:
                </p>
                <div className="flex flex-wrap gap-2">
                  {todaySchedule.map((sched, i) => (
                    <Button
                      key={i}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="bg-white border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-auto py-1.5"
                      data-testid={`schedule-suggestion-${i}`}
                      onClick={() => {
                        const clientMatch = clients.find(c => 
                          (c.name || '').trim().toLowerCase() === (sched.client_name || '').trim().toLowerCase()
                        );
                        if (clientMatch) {
                          setSelectedClientId(clientMatch.id);
                          const addresses = clientMatch.addresses || [];
                          const addrMatch = addresses.find(a => 
                            (a.address || '').trim().toLowerCase() === (sched.client_address || '').trim().toLowerCase()
                          );
                          setSelectedAddressId(addrMatch ? addrMatch.id : 'primary');
                        }
                        setFormData(prev => ({
                          ...prev,
                          client: sched.client_name,
                          project: sched.client_address,
                          description: sched.work_description || prev.description,
                        }));
                        toast.success(`Dados preenchidos: ${sched.client_name}`);
                      }}
                    >
                      <span className="font-semibold">{sched.time}</span>
                      <span className="mx-1">-</span>
                      {sched.client_name}
                      {sched.client_address && <span className="text-blue-500 ml-1">({sched.client_address})</span>}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Seletor de Funcionário (apenas para admin) */}
              {user.is_admin && (
                <div>
                  <Label className="text-gray-800 mb-2 block">Funcionário</Label>
                  <Select
                    value={formData.user_id}
                    onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                  >
                    <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                      <SelectValue placeholder="Selecione o funcionário (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('leaveBlankForSelf')}
                  </p>
                </div>
              )}

              <div>
                <Label className="text-gray-800 mb-2 block">{t('date')}</Label>
                <Input
                  data-testid="entry-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-800 mb-2 block">{t('startTime')}</Label>
                  <Input
                    data-testid="entry-start-time-input"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => {
                      setFormData({ ...formData, start_time: e.target.value });
                    }}
                    required
                    className="bg-gray-50 border-white/30 text-gray-800"
                  />
                </div>
                <div>
                  <Label className="text-gray-800 mb-2 block">{t('endTime')}</Label>
                  <Input
                    data-testid="entry-end-time-input"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => {
                      setFormData({ ...formData, end_time: e.target.value });
                    }}
                    required
                    className="bg-gray-50 border-white/30 text-gray-800"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">
                  {t('lunchBreak')} ({t('minutes')})
                </Label>
                <Input
                  type="number"
                  step="5"
                  min="0"
                  value={formData.lunch_hours}
                  onChange={(e) => setFormData({ ...formData, lunch_hours: e.target.value })}
                  placeholder="30"
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
                <p className="text-gray-400 text-xs mt-1">{t('lunchMinutesNote')}</p>
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">{t('client')} *</Label>
                <Select
                  value={selectedClientId}
                  onValueChange={(value) => {
                    const client = clients.find(c => c.id === value);
                    if (client) {
                      setSelectedClientId(value);
                      setSelectedAddressId('');
                      setFormData({
                        ...formData,
                        // Provisional totdat klant een adres-label kiest hieronder
                        client: client.name,
                        project: '',
                      });
                    }
                  }}
                  required
                >
                  <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                    <SelectValue placeholder="Selecione o bedrijf (empresa)" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedClientId && (() => {
                const client = clients.find(c => c.id === selectedClientId);
                const addresses = client?.addresses || [];

                if (addresses.length > 0) {
                  return (
                    <div>
                      <Label className="text-gray-800 mb-2 block">Klant (endereço/label) *</Label>
                      <Select
                        value={selectedAddressId}
                        onValueChange={(value) => {
                          const address = addresses.find(a => a.id === value);
                          if (address) {
                            setSelectedAddressId(value);
                            setFormData({
                              ...formData,
                              // Klant = label do endereço (eindklant), NÃO o bedrijf
                              client: address.label || client.name,
                              project: address.address,
                            });
                          } else if (value === 'primary') {
                            setSelectedAddressId('primary');
                            setFormData({
                              ...formData,
                              client: client.name,
                              project: client.address || '',
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                          <SelectValue placeholder="Selecione o klant (label ou endereço)" />
                        </SelectTrigger>
                        <SelectContent>
                          {client.address && (
                            <SelectItem value="primary">
                              {client.address} (Principal)
                            </SelectItem>
                          )}
                          {addresses.map(addr => (
                            <SelectItem key={addr.id} value={addr.id}>
                              {addr.label ? `${addr.label} — ${addr.address}` : addr.address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-gray-500 mt-1">O label do endereço é o nome real do klant (eindklant).</p>
                    </div>
                  );
                }
                return null;
              })()}
              <div>
                <Label className="text-gray-800 mb-2 block">{t('description')}</Label>
                <Input
                  data-testid="entry-description-input"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">📸 {t('workPhotos')}</Label>
                <Input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
                {selectedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-[#3da5a5] font-semibold">
                      {selectedFiles.length} {t('filesSelected')}
                    </p>
                    {selectedFiles.map((file, idx) => (
                      <p key={idx} className="text-xs text-gray-400 truncate">
                        {file.name}
                      </p>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  📷 {t('takePhotosNote')}
                </p>
              </div>

              {!editMode && (
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  {/* Parkeren (Estacionamento) */}
                  <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30 cursor-pointer hover:bg-blue-500/20 transition-colors"
                       onClick={() => setAddParkeren(!addParkeren)}>
                    <input
                      type="checkbox"
                      id="addParkeren"
                      checked={addParkeren}
                      onChange={(e) => setAddParkeren(e.target.checked)}
                      className="w-5 h-5 rounded accent-blue-500"
                    />
                    <Label htmlFor="addParkeren" className="text-gray-800 cursor-pointer flex items-center gap-2 flex-1">
                      🅿️ Adicionar Parkeren (Estacionamento)
                    </Label>
                  </div>

                  {addParkeren && (
                    <div className="space-y-3 bg-blue-500/5 p-4 rounded-lg border border-blue-500/20">
                      <div>
                        <Label className="text-gray-800 mb-2 block text-sm">Valor do Estacionamento (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={parkerenAmount}
                          onChange={(e) => setParkerenAmount(e.target.value)}
                          placeholder="0.00"
                          className="bg-gray-50 border-white/30 text-gray-800"
                        />
                      </div>
                      <p className="text-xs text-blue-400">
                        🅿️ Será criada automaticamente uma despesa de estacionamento
                      </p>
                    </div>
                  )}

                  {/* Material/Outras Despesas */}
                  <div className="flex items-center gap-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30 cursor-pointer hover:bg-orange-500/20 transition-colors"
                       onClick={() => setAddExpense(!addExpense)}>
                    <input
                      type="checkbox"
                      id="addExpense"
                      checked={addExpense}
                      onChange={(e) => setAddExpense(e.target.checked)}
                      className="w-5 h-5 rounded accent-orange-500"
                    />
                    <Label htmlFor="addExpense" className="text-gray-800 cursor-pointer flex items-center gap-2 flex-1">
                      <Wallet className="w-5 h-5 text-orange-400" />
                      Adicionar Material (despesa)
                    </Label>
                  </div>

                  {addExpense && (
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <Label className="text-gray-800 mb-2 block text-sm">Descrição do Material</Label>
                        <Input
                          type="text"
                          value={expenseData.description}
                          onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
                          placeholder="Ex: Tijolo, cimento, madeira..."
                          className="bg-gray-50 border-white/30 text-gray-800"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-800 mb-2 block text-sm">Valor (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={expenseData.amount}
                          onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                          placeholder="0.00"
                          className="bg-gray-50 border-white/30 text-gray-800"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-800 mb-2 block text-sm">Recibo (opcional)</Label>
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setExpenseData({ ...expenseData, receipt: e.target.files[0] })}
                          className="bg-gray-50 border-white/30 text-gray-800"
                        />
                      </div>
                      <p className="text-xs text-orange-400">
                        🧱 A despesa de material será registrada no mesmo cliente e data
                      </p>
                    </div>
                  )}
                </div>
              )}

              {uploadProgress && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800 text-center" data-testid="te-upload-progress">
                  {uploadProgress}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold py-6 disabled:opacity-50"
                data-testid="te-submit-btn"
              >
                {submitting ? 'Bezig...' : (editMode ? t('update') : addExpense ? t('add') + ' + ' + t('expenses') : t('add'))}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border-gray-200 shadow-sm" data-testid="time-entries-filter">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
            <div>
              <Label className="text-gray-600 text-xs mb-1 block">{t('search')} — adres / klant / medewerker</Label>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="bijv. Draaiweg, Utrecht, Nithuna…"
                  className="pl-9 bg-gray-50 border-gray-200 text-gray-800"
                  data-testid="time-entries-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-600 text-xs mb-1 block">Van</Label>
              <Input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)}
                className="bg-gray-50 border-gray-200 text-gray-800" data-testid="time-entries-start-date" />
            </div>
            <div>
              <Label className="text-gray-600 text-xs mb-1 block">Tot</Label>
              <Input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)}
                className="bg-gray-50 border-gray-200 text-gray-800" data-testid="time-entries-end-date" />
            </div>
            <Button variant="outline" onClick={() => { setSearch(''); setFilterStart(''); setFilterEnd(''); }}
              disabled={!hasFilter} className="border-gray-300 text-gray-800" data-testid="time-entries-clear-filter">
              <X className="w-4 h-4 mr-1" /> Wissen
            </Button>
          </div>
          {hasFilter && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm" data-testid="time-entries-filter-summary">
              <span className="px-3 py-1 rounded-full bg-[#3da5a5]/10 text-[#2d8585] font-semibold">
                {totalCount} {totalCount === 1 ? 'registratie' : 'registraties'}
              </span>
              <span className="px-3 py-1 rounded-full bg-[#3da5a5] text-white font-bold" data-testid="time-entries-total-hours">
                Totaal {totalHours}h
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {entries.length === 0 ? (
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-6">
              <p className="text-gray-800 text-center">{t('noEntries')}</p>
            </CardContent>
          </Card>
        ) : (
          entries.map((entry) => {
            const eind = resolveEindklant(entry);
            return (
            <Card
              key={entry.id}
              className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-gray-800 text-xl flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#3da5a5]" />
                      {formatDatePT(entry.date)}
                    </CardTitle>
                    <p className="text-gray-500 text-sm mt-1">
                      {entry.user_name} • {entry.project}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(entry.status)}
                    {entry.archived && (
                      <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                        📦 {t('archive')}
                      </span>
                    )}
                    {user.is_admin && !entry.archived && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDialog(entry)}
                        className="bg-blue-600 hover:bg-blue-700 text-white border-none"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {(user.is_admin || (entry.status === 'pending' && entry.user_id === user.id)) && !entry.archived && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(entry.id, entry.status)}
                        className="bg-red-600 hover:bg-red-700 text-white border-none"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#3da5a5]/10 p-3 rounded-lg">
                    <p className="text-gray-500 text-sm">{t('time')}</p>
                    <p className="text-gray-800 font-medium">
                      {entry.start_time} - {entry.end_time}
                    </p>
                  </div>
                  <div className="bg-[#3da5a5]/10 p-3 rounded-lg">
                    <p className="text-gray-500 text-sm">{t('workHours')}</p>
                    <p className="text-gray-800 font-medium">{entry.hours_worked}h</p>
                  </div>
                  {entry.lunch_hours > 0 && (
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">{t('lunchBreak')}</p>
                      <p className="text-gray-800 font-medium">{entry.lunch_hours} min</p>
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-400 text-sm">Klant</p>
                  <p className="text-gray-800 font-semibold">{eind.label || entry.client}</p>
                  {eind.bedrijf && eind.bedrijf.toLowerCase() !== (eind.label || entry.client || '').toLowerCase() && (
                    <p className="text-gray-500 text-[11px]">Bedrijf: {eind.bedrijf}</p>
                  )}
                </div>
                {entry.description && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-400 text-sm">{t('description')}</p>
                    <p className="text-gray-800">{entry.description}</p>
                  </div>
                )}
                {entry.files && entry.files.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-400 text-sm mb-3">📷 {t('photos')} ({entry.files.length})</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {entry.files.map((file) => (
                        <button
                          key={file.id}
                          onClick={() => handleDownload(entry.id, file.id, file.filename)}
                          className="group relative bg-[#3da5a5]/20 hover:bg-[#3da5a5]/40 border-2 border-[#3da5a5]/50 rounded-lg p-3 transition-all duration-300 hover:scale-105"
                        >
                          <div className="flex flex-col items-center gap-2">
                            {getFileIcon(file.file_type)}
                            <span className="text-xs text-gray-800 truncate w-full text-center">
                              {file.filename.length > 15 ? file.filename.substring(0, 12) + '...' : file.filename}
                            </span>
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                            <Download className="w-6 h-6 text-white" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TimeEntries;
